import * as THREE from 'three';
import type { SketchFace, SketchSolid, SketchVec3 } from './types';
import { planeBasis } from './triangulate';
import { signedArea } from './faceSplit';

/**
 * Entity measurements — Entity Info panel derivations (never stored).
 *
 * faceArea: projected loop areas (outer minus holes) on the face plane.
 * solidVolume: signed tetrahedra over the triangulated shell (divergence
 * theorem) — exact for the analytic closed solids the sketch engine commits.
 */

const project2D = (
    loop: SketchVec3[],
    origin: THREE.Vector3,
    u: THREE.Vector3,
    v: THREE.Vector3
): Array<{ x: number; y: number }> =>
    loop.map((p) => {
        const d = new THREE.Vector3(...p).sub(origin);
        return { x: d.dot(u), y: d.dot(v) };
    });

/** World-space area of one planar face (outer minus holes), squared units. */
export function faceArea(face: SketchFace): number {
    if (face.outer.length < 3) return 0;
    const normal = new THREE.Vector3(...face.normal).normalize();
    const origin = new THREE.Vector3(...face.origin);
    const { u, v } = planeBasis(normal);
    let area = Math.abs(signedArea(project2D(face.outer, origin, u, v)));
    for (const hole of face.holes) {
        if (hole.length >= 3) area -= Math.abs(signedArea(project2D(hole, origin, u, v)));
    }
    return Math.max(area, 0);
}

/**
 * Enclosed volume of a solid via Σ det(a, b, c) / 6 over its triangulated
 * faces. Correct for closed shells (the only state the engine commits);
 * open shells return a best-effort magnitude, never used for decisions.
 */
export function solidVolume(solid: SketchSolid): number {
    let volume6 = 0;
    for (const face of solid.faces) {
        if (face.outer.length < 3) continue;
        const normal = new THREE.Vector3(...face.normal).normalize();
        const origin = new THREE.Vector3(...face.origin);
        const { u, v } = planeBasis(normal);
        const contour = project2D(face.outer, origin, u, v);
        const holes = face.holes
            .filter((h) => h.length >= 3)
            .map((h) => project2D(h, origin, u, v));
        let triangles: number[][];
        try {
            triangles = THREE.ShapeUtils.triangulateShape(contour, holes);
        } catch {
            continue;
        }
        const pool = [face.outer, ...face.holes.filter((h) => h.length >= 3)];
        const lift = (idx: number): THREE.Vector3 => {
            let remaining = idx;
            let loop = 0;
            while (loop < pool.length && remaining >= pool[loop].length) {
                remaining -= pool[loop].length;
                loop++;
            }
            const p = pool[loop]?.[remaining] ?? face.outer[0];
            return new THREE.Vector3(...p);
        };
        for (const tri of triangles) {
            const a = lift(tri[0]);
            const b = lift(tri[1]);
            const c = lift(tri[2]);
            volume6 += a.dot(new THREE.Vector3().crossVectors(b, c));
        }
    }
    return Math.abs(volume6 / 6);
}
